<template>
  <v-select
    :value="value"
    :items="items"
    :label="label"
    :placeholder="placeholder"
    :item-text="itemText"
    :item-value="itemValue"
    multiple
    dense
    outlined
    single-line
    clearable
    hide-details
    :menu-props="{ closeOnContentClick: false }"
    @input="onInput"
    @click:clear="onClear"
    @blur="flush"
  >
    <template v-if="showSelectAll && items.length" #prepend-item>
      <v-list-item ripple @mousedown.prevent @click="toggleAll">
        <v-list-item-action>
          <LucideIcon :name="allIcon" :size="18" :color="allColor" />
        </v-list-item-action>
        <v-list-item-content>
          <v-list-item-title class="kf-ms__all">Select all</v-list-item-title>
        </v-list-item-content>
      </v-list-item>
      <v-divider class="mt-1" />
    </template>

    <!-- One summary instead of N chips: with a dozen filters on screen the
         chip lists wrap and the filter row grows taller than the table. -->
    <template #selection="{ index }">
      <span v-if="index === 0" class="kf-ms__summary">
        {{ selected.length }} selected
      </span>
    </template>
  </v-select>
</template>

<script>
/**
 * Multi-select with a "Select all" row.
 *
 * Two things it fixes versus the legacy screens' copy-pasted version:
 *
 *  1. The select-all logic lives here once, keyed on nothing. The legacy pages
 *     carry four helpers each taking (filterKey, optionsKey) string pairs, and
 *     getting that pair wrong is silent -- which is how the old screens ended
 *     up with filters that render and do nothing.
 *
 *  2. It emits `change` when the MENU CLOSES, not on every tick. The legacy
 *     version fires a list request AND a counts request per checkbox, so
 *     ticking six values is twelve round trips.
 */
export default {
  name: "KfMultiSelect",
  props: {
    value: { type: Array, default: () => [] },
    items: { type: Array, default: () => [] },
    label: { type: String, default: "" },
    /** Shown when nothing is selected -- an empty box reads as broken. */
    placeholder: { type: String, default: "Any" },
    itemText: { type: String, default: undefined },
    itemValue: { type: String, default: undefined },
    showSelectAll: { type: Boolean, default: true },
  },
  data() {
    return { dirty: false };
  },
  computed: {
    // Vuetify's `clearable` hands back null rather than [], which would then
    // break every .length below.
    selected() {
      return this.value || [];
    },
    optionValues() {
      if (!this.itemValue) return this.items;
      return this.items.map((i) => (i && typeof i === "object" ? i[this.itemValue] : i));
    },
    allSelected() {
      return this.optionValues.length > 0 && this.selected.length === this.optionValues.length;
    },
    someSelected() {
      return this.selected.length > 0 && !this.allSelected;
    },
    allIcon() {
      if (this.allSelected) return "square-check";
      if (this.someSelected) return "square-minus";
      return "square";
    },
    allColor() {
      return this.allSelected || this.someSelected ? "#194e76" : "#54636f";
    },
  },
  beforeDestroy() {
    clearTimeout(this.timer);
  },
  methods: {
    onInput(value) {
      this.$emit("input", value || []);
      this.queue();
    },
    onClear() {
      this.$emit("input", []);
      this.flush();
    },
    toggleAll() {
      this.$emit("input", this.allSelected ? [] : this.optionValues.slice());
      this.queue();
    },
    /**
     * Coalesce a burst of ticks into one `change`.
     *
     * Deliberately a timer rather than "emit when the menu closes": Vuetify 2's
     * VSelect exposes no menu-close event, so hooking one would mean listening
     * for something that never fires -- a filter that looks wired and silently
     * never reloads.
     */
    queue() {
      this.dirty = true;
      clearTimeout(this.timer);
      this.timer = setTimeout(this.flush, 450);
    },
    /** Emit now if anything is pending -- on blur, or when the timer expires. */
    flush() {
      clearTimeout(this.timer);
      if (!this.dirty) return;
      this.dirty = false;
      this.$emit("change");
    },
  },
};
</script>

<style scoped>
.kf-ms__summary {
  font-size: var(--kf-fs-hint);
  color: #000;
}
.kf-ms__all {
  font-size: var(--kf-fs-hint);
  font-weight: 600;
}
</style>
